import { useCallback, useEffect, useId, useRef, useState } from "react";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Field, Input, Select } from "../ui";
import { useSession } from "./session";
import { apiCall } from "../core/api";

interface Installed {
  bundleId: string;
  version: string;
  title: string;
  installedAt: string;
}

interface Report {
  bundleId: string;
  version: string;
  dryRun: boolean;
  created: Record<string, string[]>;
  updated: Record<string, string[]>;
  unchanged: Record<string, string[]>;
  skipped: Record<string, string[]>;
  bound: Record<string, string>;
  renamed: Record<string, string>;
  plugins: { present: string[]; missing: string[] };
  scopes: string[];
}

interface Named {
  id: string;
  title: string;
  slug?: string;
}

const KINDS = ["propDefs", "propsets", "dashboards", "automations", "scripts"] as const;
const POLICIES = ["skip", "rename", "replace"] as const;

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function download(name: string, base64: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/zip" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Settings → Bundles (spec §9.14, level `power`): package dashboards, propsets and automations to share,
 * and bring bundles in. An import is always previewed first; nothing changes until it is confirmed.
 */
export function BundlesSettings() {
  const { t, i18n } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const [installed, setInstalled] = useState<Installed[]>([]);
  const uid = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dashboards, setDashboards] = useState<Named[]>([]);
  const [automations, setAutomations] = useState<Named[]>([]);
  const [propsets, setPropsets] = useState<Named[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bundleId, setBundleId] = useState("");
  const [title, setTitle] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pending, setPending] = useState<{ data: string; name: string } | null>(null);
  const [policy, setPolicy] = useState<(typeof POLICIES)[number]>("skip");
  const [report, setReport] = useState<Report | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(
    () =>
      workspaceId
        ? Promise.all([
            apiCall<{ bundles: Installed[] }>(workspaceId, "GET", "/bundles").catch(() => ({ bundles: [] })),
            apiCall<{ dashboards: Named[] }>(workspaceId, "GET", "/dashboards").catch(() => ({
              dashboards: [],
            })),
            apiCall<{ automations: Named[] }>(workspaceId, "GET", "/automations").catch(() => ({
              automations: [],
            })),
            apiCall<{ propsets: Named[] }>(workspaceId, "GET", "/propsets").catch(() => ({ propsets: [] })),
          ]).then(([bs, d, a, p]) => {
            setInstalled(bs.bundles);
            setDashboards(d.dashboards);
            setAutomations(a.automations);
            setPropsets(p.propsets);
          })
        : Promise.resolve(),
    [workspaceId],
  );
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const fail = (e: unknown) =>
    setMessage(t(`errors.${(e as Error).message}`, { defaultValue: t("error.generic") }));

  const pick = (key: string, on: boolean) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const chosen = (kind: string) =>
    [...picked].filter((k) => k.startsWith(kind + ":")).map((k) => k.slice(kind.length + 1));

  const exportBundle = async () => {
    if (!workspaceId) return;
    setMessage(null);
    try {
      const r = await apiCall<{ filename: string; data: string; warnings: { code: string }[] }>(
        workspaceId,
        "POST",
        "/bundles/export",
        {
          bundleId: bundleId.trim(),
          title: title.trim(),
          dashboards: chosen("d"),
          automations: chosen("a"),
          propsets: chosen("p"),
        },
      );
      setWarnings(
        r.warnings.map((w) => t(`bundles.warning.${w.code.split(".")[1]}`, { defaultValue: w.code })),
      );
      download(r.filename, r.data);
    } catch (e) {
      fail(e);
    }
  };

  const preview = async (file: File) => {
    if (!workspaceId) return;
    setMessage(null);
    const data = toBase64(await file.arrayBuffer());
    setPending({ data, name: file.name });
    try {
      setReport(
        await apiCall<Report>(workspaceId, "POST", "/bundles/import", {
          data,
          dryRun: true,
          collides: policy,
        }),
      );
    } catch (e) {
      setPending(null);
      setReport(null);
      fail(e);
    }
  };

  const confirmImport = async () => {
    if (!workspaceId || !pending) return;
    try {
      const r = await apiCall<Report>(workspaceId, "POST", "/bundles/import", {
        data: pending.data,
        collides: policy,
      });
      setMessage(t("bundles.imported", { id: r.bundleId, version: r.version }));
      setPending(null);
      setReport(null);
      await refresh();
    } catch (e) {
      fail(e);
    }
  };

  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  const count = (group: Record<string, string[]>) => KINDS.reduce((n, k) => n + (group[k]?.length ?? 0), 0);

  return (
    <SettingsSection
      id="settings-bundles"
      intro={t("bundles.hint")}
      summary={t("settings.summary.bundles")}
      title={t("bundles.title")}
      testId="settings-bundles"
      folded
    >
      {installed.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t("bundles.installed")}>
          {installed.map((b) => (
            <li key={b.bundleId}>
              <span className="text-[12.5px]">
                {b.title || b.bundleId}{" "}
                <StatusText tone="quiet">
                  {b.version} · {fmt.format(new Date(b.installedAt))}
                </StatusText>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
        <h3 className="text-[13px] font-medium">{t("bundles.import")}</h3>
        <div className="flex flex-wrap items-end gap-2">
          <Field htmlFor={`${uid}-1`} label={t("bundles.file")}>
            <input
              ref={fileRef}
              id={`${uid}-1`}
              type="file"
              accept=".zip,application/zip"
              className="sr-only"
              data-testid="bundle-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void preview(f);
                e.target.value = ""; // the same file may be chosen again
              }}
            />
            <Button variant="secondary" onClick={() => fileRef.current?.click()}>
              {t("bundles.chooseFile")}
            </Button>
          </Field>
          <Field htmlFor={`${uid}-2`} label={t("bundles.collides")}>
            <Select
              id={`${uid}-2`}
              value={policy}
              onChange={(e) => setPolicy(e.target.value as typeof policy)}
            >
              {POLICIES.map((p) => (
                <option key={p} value={p}>
                  {t(`bundles.policy.${p}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {report && pending && (
          <div
            className="flex flex-col gap-1 rounded-md border border-accent/40 bg-accent/5 p-3"
            data-testid="bundle-preview"
          >
            <p className="text-[13px] font-medium">
              {report.bundleId} {report.version}
            </p>
            <p className="text-[12.5px]">
              {t("bundles.previewCounts", {
                created: count(report.created),
                updated: count(report.updated),
                unchanged: count(report.unchanged),
                skipped: count(report.skipped),
              })}
            </p>
            {Object.keys(report.bound).length > 0 && (
              <p className="text-[12px] text-neutral-500">
                {t("bundles.bound", { n: Object.keys(report.bound).length })}
              </p>
            )}
            {report.scopes.length > 0 && (
              <p className="text-[12px]">{t("bundles.scopes", { scopes: report.scopes.join(", ") })}</p>
            )}
            {report.plugins.missing.length > 0 && (
              <p className="text-[12px]">
                {t("bundles.missingPlugins", { plugins: report.plugins.missing.join(", ") })}
              </p>
            )}
            <div className="mt-1 flex gap-2">
              <Button size="sm" onClick={() => void confirmImport()} data-testid="bundle-confirm">
                {t("bundles.confirm")}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setPending(null);
                  setReport(null);
                }}
              >
                {t("bundles.cancel")}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
        <h3 className="text-[13px] font-medium">{t("bundles.export")}</h3>
        <div className="flex flex-wrap gap-2">
          <Field htmlFor={`${uid}-3`} label={t("bundles.id")} hint={t("bundles.idHint")}>
            <Input
              id={`${uid}-3`}
              value={bundleId}
              onChange={(e) => setBundleId(e.target.value.toLowerCase())}
              placeholder="home-chores"
              className="font-mono"
              data-testid="bundle-id"
            />
          </Field>
          <Field htmlFor={`${uid}-4`} label={t("bundles.bundleTitle")}>
            <Input id={`${uid}-4`} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        </div>
        {(
          [
            ["d", "dashboards", dashboards],
            ["p", "propsets", propsets],
            ["a", "automations", automations],
          ] as const
        ).map(([k, label, list]) =>
          list.length === 0 ? null : (
            <fieldset key={k} className="flex flex-wrap gap-x-4 gap-y-1">
              <legend className="mb-1 text-[11.5px] text-neutral-500">{t(`bundles.pick.${label}`)}</legend>
              {list.map((x) => (
                <span key={x.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={picked.has(`${k}:${x.id}`)}
                    label={x.title || x.slug || x.id}
                    onChange={(v) => pick(`${k}:${x.id}`, v)}
                  />
                  <span className="text-[13px]" aria-hidden>
                    {x.title || x.slug || x.id}
                  </span>
                </span>
              ))}
            </fieldset>
          ),
        )}
        <div>
          <Button
            onClick={() => void exportBundle()}
            disabled={!/^[a-z0-9][a-z0-9-]{1,62}$/.test(bundleId) || picked.size === 0}
            data-testid="bundle-export"
          >
            {t("bundles.download")}
          </Button>
        </div>
        {warnings.map((w) => (
          <p key={w} className="text-[12px] text-neutral-500">
            {w}
          </p>
        ))}
      </div>
      {message && (
        <p className="text-[12.5px]" role="status" data-testid="bundle-message">
          {message}
        </p>
      )}
    </SettingsSection>
  );
}
